// Client-side presentation for the per-plan feature modules.
//
// The registry on the backend owns the truth — keys, labels, descriptions,
// categories, which need an acknowledgement — and ships all of it down with the
// catalog so a copy change does not need a mobile release. What CANNOT travel
// down that pipe is anything the mobile app alone knows: which icon to draw,
// what colour to tint it, and which route the "jump to the feature" hand-off
// should land on. That is what this file is, and nothing more.
//
// If a module is missing from here it still renders, with a neutral fallback
// icon and no jump target. A new module on the server is a slightly plain row,
// never a crash and never an invisible feature.

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

export interface ModulePresentation {
  icon: IconProp;
  color: string;
  /**
   * Route to open after this module is switched on. Names match the PlansStack
   * registrations in MainTabNavigator; every one takes { projectId }.
   *
   * Absent means the module has no screen of its own — Phone Calls is reached
   * by asking Ellie and by people ringing in, Apple Watch and CarPlay are
   * companion surfaces configured on the device, and Backups runs on its own.
   * Those rows simply do not offer a jump.
   */
  screen?: string;
}

const FALLBACK: ModulePresentation = { icon: ['fad', 'boxes-stacked'], color: '#64748B' };

// Colours are literals rather than theme tokens on purpose: these are per-module
// identity tints (the same idea as NAV_PERSONA_OPTIONS' `color`), read on both
// light and dark surfaces at 0x18 alpha behind the glyph.
const PRESENTATION: Record<string, ModulePresentation> = {
  // ── Care ────────────────────────────────────────────────────────────────
  'mod:medications':      { icon: ['fad', 'pills'],             color: '#EF4444', screen: 'ProjectMedications' },
  'mod:calendar':         { icon: ['fad', 'calendar-days'],     color: '#3B82F6', screen: 'ProjectCalendar' },
  'mod:reminders':        { icon: ['fad', 'bell'],              color: '#F59E0B', screen: 'ProjectReminders' },
  'mod:care_suggestions': { icon: ['fad', 'lightbulb-on'],      color: '#8B5CF6', screen: 'AgentSuggestions' },

  // ── Health ──────────────────────────────────────────────────────────────
  'mod:diagnoses':        { icon: ['fad', 'notes-medical'],     color: '#14B8A6', screen: 'ProjectDiagnoses' },
  'mod:health_profile':   { icon: ['fad', 'user-doctor'],       color: '#0EA5E9', screen: 'ProjectHealthProfile' },
  'mod:connected_health': { icon: ['fad', 'heart-pulse'],       color: '#EC4899', screen: 'ProjectHealthIntegration' },
  'mod:health_records':   { icon: ['fad', 'file-medical'],      color: '#06B6D4', screen: 'ProjectFasten' },

  // ── Communication ───────────────────────────────────────────────────────
  'mod:inbox':            { icon: ['fad', 'inbox'],             color: '#6366F1', screen: 'ProjectInbox' },
  'mod:team_chat':        { icon: ['fad', 'comments'],          color: '#22C55E', screen: 'ProjectTeam' },
  // No screen: Text Messaging is settings rows and a delivery-method choice,
  // not a destination. ProjectCommunicate looks like a fit but belongs to
  // mod:team_chat, so jumping there would land the user on a disabled-module
  // screen for a DIFFERENT feature whenever team chat happens to be off.
  'mod:sms':              { icon: ['fad', 'message-sms'],       color: '#10B981' },
  // No screen: reached by asking Ellie to call, and by people ringing in.
  'mod:phone_voice':      { icon: ['fad', 'phone-volume'],             color: '#0891B2' },
  'mod:weekly_summary':   { icon: ['fad', 'newspaper'],         color: '#A855F7', screen: 'ProjectWeeklyBriefing' },

  // ── Safety ──────────────────────────────────────────────────────────────
  'mod:emergency':        { icon: ['fad', 'shield-heart'],       color: '#DC2626', screen: 'EmergencySOSSetup' },
  'mod:vault':            { icon: ['fad', 'vault'],             color: '#475569', screen: 'ProjectVault' },
  // No screen: backups run on their own, with no surface to land on.
  'mod:snapshots':        { icon: ['fad', 'camera'],      color: '#78716C' },
  'mod:location':         { icon: ['fad', 'location-dot'],      color: '#F97316' },

  // ── Assist ──────────────────────────────────────────────────────────────
  'mod:surveys':          { icon: ['fad', 'list-check'],        color: '#8B5CF6', screen: 'ProjectSurveys' },
  'mod:benefits':         { icon: ['fad', 'shield-check'],      color: '#16A34A', screen: 'ProjectBenefits' },
  'mod:contacts':         { icon: ['fad', 'address-book'],      color: '#0284C7', screen: 'ProjectContacts' },

  // ── Device ──────────────────────────────────────────────────────────────
  // Watch and CarPlay are configured on the device itself, not on a plan screen.
  'mod:watch':            { icon: ['fad', 'watch-apple'],       color: '#334155' },
  'mod:carplay':          { icon: ['fad', 'car'],               color: '#1D4ED8' },
  'mod:siri':             { icon: ['fad', 'microphone-lines'],  color: '#7C3AED', screen: 'SiriShortcuts' },
};

/** Presentation for a module key. Unknown keys get a neutral fallback. */
export function modulePresentation(key: string): ModulePresentation {
  return Object.prototype.hasOwnProperty.call(PRESENTATION, key)
    ? PRESENTATION[key]
    : FALLBACK;
}

/** Human labels for the registry's six category keys, in display order. */
export const CATEGORY_LABELS: Record<string, string> = {
  care: 'Everyday care',
  health: 'Health',
  communication: 'Staying in touch',
  safety: 'Safety & records',
  assist: 'Help and guidance',
  device: 'Devices',
};

export const CATEGORY_ORDER = ['care', 'health', 'communication', 'safety', 'assist', 'device'];

// ── Which module owns which route ──────────────────────────────────────────
//
// Derived by walking every navigation entry point in the app — the two
// PROJECT_CATEGORIES lists, linking.ts deep links, RootNavigator's notification
// handlers, home cards and settings rows — and asking which module each screen
// belongs to. Only screens a module genuinely OWNS are listed. A screen that
// merely links to a module's feature is not the module's screen.
//
// Everything absent from this map is deliberately ungated, in three groups:
//
//   Core       ProjectChat, Conversation, EllieVoice, ProjectHome, ProjectFiles,
//              ProjectMembers, ProjectSettings, ProjectKnowledgeCoverage,
//              ProjectsList, CreatePlan. These are what the app IS. Ellie chat
//              in particular is core and everything else revolves around it, so
//              no module may hide it. EllieVoice is in-app realtime voice, which
//              belongs to Ellie and is gated by ai_realtime_voice_enabled —
//              NOT by mod:phone_voice, which is the PSTN dial capability
//              (plan_voice) and has no screen of its own.
//
//   Not ours   Ingestion (data processing, which is never gated — a file the
//              user sends is always read and filed), ProjectCadenceSession
//              (Cadence), PlanTasks, ProjectHistory.
//
//   Shared     ProjectSmartReminders is the landing point for TWO modules'
//              notifications: RootNavigator sends it detailKind
//              'smart_reminder' (mod:reminders) and detailKind 'health_alert'
//              (mod:connected_health), from both the warm and cold-start paths.
//              Gating the route on either module would silently break the
//              other's notification, so it stays open. Splitting it is real
//              work and belongs in its own change, not smuggled into this one.
export const SCREEN_MODULE: Record<string, string> = {
  ProjectMedications:           'mod:medications',
  ProjectCalendar:              'mod:calendar',
  ProjectCalendarSettings:      'mod:calendar',
  ProjectAppleCalendarSettings: 'mod:calendar',
  ProjectReminders:             'mod:reminders',
  AgentSuggestions:             'mod:care_suggestions',
  ProjectDiagnoses:             'mod:diagnoses',
  ProjectHealthProfile:         'mod:health_profile',
  ProjectHealthIntegration:     'mod:connected_health',
  ProjectHealthAlerts:          'mod:connected_health',
  Wellness:                     'mod:connected_health',
  ProjectFasten:                'mod:health_records',
  ProjectInbox:                 'mod:inbox',
  ProjectInboxSetup:            'mod:inbox',
  ProjectEmailSettings:         'mod:inbox',
  // ProjectTeam and ProjectContacts are the SAME component (ContactsScreen) on
  // two routes with different tabs, which is why the guard is applied at route
  // registration rather than at the component's default export — one export
  // cannot carry two different modules.
  ProjectTeam:                  'mod:team_chat',
  ProjectCommunicate:           'mod:team_chat',
  ProjectMemberChat:            'mod:team_chat',
  ProjectContacts:              'mod:contacts',
  ProjectWeeklyBriefing:        'mod:weekly_summary',
  EmergencySOSSetup:            'mod:emergency',
  ProjectVault:                 'mod:vault',
  ProjectSnapshots:             'mod:snapshots',
  ProjectSurveys:               'mod:surveys',
  ProjectBenefits:              'mod:benefits',
  SiriShortcuts:                'mod:siri',
};

/** The module that owns a route, or null when the route is not module-gated. */
export function moduleForScreen(screen: string): string | null {
  return Object.prototype.hasOwnProperty.call(SCREEN_MODULE, screen)
    ? SCREEN_MODULE[screen]
    : null;
}

/**
 * Drops sub-items whose module is off, then drops any category left empty.
 *
 * Shared by TwoLevelTabBar and DesktopSidebar so the two cannot disagree about
 * what is visible. It takes `isOn` rather than reading the store itself, so it
 * stays a pure function that a test can drive directly.
 *
 * A category with a `directScreen` and no items (Ellie) is never dropped — it
 * has nothing to filter and it is core.
 */
export function filterCategoriesByModule<
  T extends { key: string; items: Array<{ screen: string }>; directScreen?: string },
>(categories: T[], isOn: (moduleKey: string) => boolean): T[] {
  const out: T[] = [];
  for (const cat of categories) {
    if (cat.directScreen && !cat.items.length) {
      const owner = moduleForScreen(cat.directScreen);
      if (!owner || isOn(owner)) out.push(cat);
      continue;
    }
    const items = cat.items.filter((it) => {
      const owner = moduleForScreen(it.screen);
      return !owner || isOn(owner);
    });
    // An empty segment is worse than a missing one: it renders a tab that
    // leads to a blank row of nothing.
    if (items.length) out.push({ ...cat, items });
  }
  return out;
}
